import React from 'react';
import ReactDOM from 'react-dom/client';
import { ConfigProvider, theme as antdTheme } from 'antd';
import zhCN from 'antd/locale/zh_CN';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider } from 'react-router-dom';
import { router } from './App';
import { AuthProvider } from './auth/AuthContext';
import { GAIN_GREEN, LOSS_RED } from './theme/colors';
import './styles.css';

// The WebSocket is JWT-gated and (dis)connected by AuthProvider as the
// session changes — no eager connect at module load.

const queryClient = new QueryClient({
  defaultOptions: { queries: { staleTime: 5_000, refetchOnWindowFocus: false } },
});

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <ConfigProvider
      locale={zhCN}
      theme={{
        // Vercel / Geist design language — light, almost monochrome.
        // Background is a near-white wash; surfaces are pure white with
        // hair-thin grey borders (no shadows). Primary action is pure
        // black (Vercel's signature). Only chroma is reserved for
        // gain-green / loss-red so financial colour-language still pops.
        algorithm: antdTheme.defaultAlgorithm,
        token: {
          colorPrimary: '#000000',
          colorBgLayout: '#fafafa',
          colorBgContainer: '#ffffff',
          colorBgElevated: '#ffffff',
          colorBorder: '#eaeaea',
          colorBorderSecondary: '#f0f0f0',
          colorText: '#000000',
          colorTextSecondary: '#666666',
          colorTextTertiary: '#999999',
          colorSuccess: GAIN_GREEN,
          colorError: LOSS_RED,
          colorWarning: '#f5a623',
          // Info callouts (e.g. the "什么是回撤" Alert) drop the chroma —
          // a near-white wash with a black icon + black text matches the
          // Vercel "Note" pattern.
          colorInfo: '#000000',
          colorInfoBg: '#fafafa',
          colorInfoBorder: '#eaeaea',
          colorInfoText: '#000000',
          // Vercel monochrome: links resolve to pure black instead of the
          // accent blue — the pointer cursor + hover underline already
          // carries the "clickable" affordance.
          colorLink: '#000000',
          colorLinkHover: '#333333',
          colorLinkActive: '#000000',
          // Select / Cascader / TreeSelect dropdown — antd derives the
          // "selected option" background from colorPrimary by tint(). Pure
          // black has no hue to tint, so the active row comes out
          // near-black and swallows the text. Force monochrome greys.
          controlItemBgActive: '#f4f4f5',
          controlItemBgActiveHover: '#e4e4e7',
          controlItemBgHover: '#f4f4f5',
          borderRadius: 6,
          fontFamily:
            '-apple-system, BlinkMacSystemFont, "Inter", "Segoe UI", Roboto, sans-serif',
        },
        components: {
          Layout: {
            headerBg: '#ffffff',
            bodyBg: '#fafafa',
            footerBg: '#ffffff',
            siderBg: '#ffffff',
          },
          Menu: {
            itemBg: '#ffffff',
            subMenuItemBg: '#fafafa',
            itemSelectedBg: '#f4f4f5',
            itemSelectedColor: '#000000',
          },
          Card: {
            colorBorderSecondary: '#eaeaea',
          },
        },
      }}
    >
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <RouterProvider router={router} />
        </AuthProvider>
      </QueryClientProvider>
    </ConfigProvider>
  </React.StrictMode>,
);
